import { PERSIAN_MONTHS } from './dataHelpers';
import { toPersianDigits } from './formatters';

export interface JalaliDateParts {
  year: number;
  month: number;
  day: number;
}

const toEnglishDigits = (value: string): string =>
  value.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d)));

export function getJalaliFromDate(date: Date): JalaliDateParts {
  const parts = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(date);

  const get = (type: string) =>
    parseInt(toEnglishDigits(parts.find((part) => part.type === type)?.value || '0'), 10);

  return { year: get('year'), month: get('month'), day: get('day') };
}

export function getJalaliNow(): JalaliDateParts {
  return getJalaliFromDate(new Date());
}

function isJalaliLeapYear(jy: number): boolean {
  // Algorithm aligned with jalaali-js (jalaali calendar leap rule).
  const breaks = [-61, 9, 38, 199, 426, 686, 756, 818, 1111, 1181, 1210, 1635, 2060, 2097, 2192, 2262, 2324, 2394, 2456, 3178];
  let jump = 0;
  let jp = breaks[0];

  for (let i = 1; i < breaks.length; i++) {
    const jm = breaks[i];
    jump = jm - jp;
    if (jy < jm) break;
    jp = jm;
  }

  let n = jy - jp;
  if (jump - n < 6) {
    n = n - jump + Math.floor((jump + 4) / 33) * 33;
  }
  let leap = (((n + 1) % 33) - 1) % 4;
  if (leap < 0) leap += 4;
  return leap === 0;
}

export function getJalaliMonthDays(year: number, month: number): number {
  if (month <= 6) return 31;
  if (month <= 11) return 30;
  return isJalaliLeapYear(year) ? 30 : 29;
}

export function formatJalaliDate({ year, month, day }: JalaliDateParts): string {
  const monthName = PERSIAN_MONTHS[month - 1] ?? '';
  return `${toPersianDigits(day)} ${monthName} ${toPersianDigits(year)}`;
}

export function getJalaliYesterday(): JalaliDateParts {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return getJalaliFromDate(yesterday);
}

export function getLastDayOfPreviousJalaliMonth(): JalaliDateParts {
  const now = getJalaliNow();
  let year = now.year;
  let month = now.month - 1;

  if (month < 1) {
    month = 12;
    year -= 1;
  }

  return {
    year,
    month,
    day: getJalaliMonthDays(year, month),
  };
}

export function getLastDataUpdateInfo(hasPeriodSelector: boolean): {
  formattedDate: string;
  hint: string | null;
} {
  const dateParts = hasPeriodSelector ? getJalaliYesterday() : getLastDayOfPreviousJalaliMonth();
  const formattedDate = formatJalaliDate(dateParts);

  return {
    formattedDate,
    hint: hasPeriodSelector ? 'دیروز' : 'آخر ماه گذشته',
  };
}
